// @givmo/board — meeting decode + display helpers.
//
// ══ THE NOTICE BLOCK IS DISPLAYED, NEVER COMPOSED ═══════════════════════════════════════════════
//
// `BoardMeetingNoticeOut.statement` is a sentence the BACKEND renders, and this module puts it on
// screen verbatim. That is not deference for its own sake — the sentence is carefully built to state
// its own method and to avoid a conclusion the product is not entitled to draw:
//
//   "Against the notice period you recorded (10 days, Art. III §4), this meeting was scheduled 6 days
//    ahead — 4 days fewer than that period, counting calendar days from the day it was scheduled to
//    the day of the meeting in America/New_York."
//
// There is deliberately NO field meaning "this meeting was noticed correctly", and a boolean spelled
// any other way would be read as one (plan §3.4). `is_short_of_recorded_period` is a SHORTFALL FLAG,
// not a verdict: it never blocks anything, and the UI presents it as information beside the sentence
// rather than as an error state.
//
// When the organization has recorded no notice period, every figure is null and the statement says so and
// stops — "No notice period has been recorded for a regular meeting, so no notice count is shown."
// Volunteering a day count with nothing to compare it against invites exactly the conclusion the
// module may not draw, so the UI must not fill that gap with one either.

import { extractList } from '@givmo/api-client/list';
import { humanize } from '../../lib/display';
import { officerRolesWords as roles } from '../roster/officer-roles-words';
import { committeeMeetingsWords as cm } from '../committees/committee-meetings-words';
export { displayDate } from '../../lib/display';
import type {
  OrgBoardAttendanceIntentOut,
  OrgBoardMeetingOut,
  OrgBoardMemberOut,
} from '@givmo/api-client';
import type { BadgeProps } from '@givmo/design-system';

type BadgeTone = NonNullable<BadgeProps['tone']>;

/**
 * Meetings from `GET /board/{org_ref}/meetings`. The handler is annotated `-> dict`
 * so the wire is the paginated envelope, which the api-client middleware unwraps to a bare array at
 * runtime — `extractList` reads either shape, or `[]`, and never throws.
 */
export function extractMeetings(body: unknown): OrgBoardMeetingOut[] {
  return extractList<OrgBoardMeetingOut>(body, 'data');
}

/** Upcoming meetings soonest-first, then past meetings most-recent-first. */
export function sortMeetings(meetings: readonly OrgBoardMeetingOut[]): OrgBoardMeetingOut[] {
  const now = Date.now();
  return [...meetings].sort((left, right) => {
    const leftAt = instant(left.starts_at);
    const rightAt = instant(right.starts_at);
    const leftUpcoming = leftAt >= now;
    const rightUpcoming = rightAt >= now;
    if (leftUpcoming !== rightUpcoming) return leftUpcoming ? -1 : 1;
    return leftUpcoming ? leftAt - rightAt : rightAt - leftAt;
  });
}

/** Human label for a meeting type. */
export function meetingTypeLabel(meetingType: string | null | undefined): string {
  switch ((meetingType ?? '').toLowerCase()) {
    case 'regular':
      return 'Regular';
    case 'annual':
      return 'Annual';
    case 'special':
      return 'Special';
    default:
      return humanize(meetingType);
  }
}

/** Badge tone for a meeting status. PURELY VISUAL. */
export function statusTone(status: string | null | undefined): BadgeTone {
  return (status ?? '').toLowerCase() === 'cancelled' ? 'neutral' : 'success';
}

/**
 * Whether a meeting has STARTED: its start is known and is at or before `nowMs` (CBG-FIX25). The
 * status has no "held" (a meeting that happened is still `scheduled`), so the start decides: from
 * then on nobody is asked whether they can make it, and the meeting is no longer "Scheduled". An
 * unknown or unreadable start has not started.
 */
export function meetingHasStarted(startsAt: string | null | undefined, nowMs: number): boolean {
  if (!startsAt) return false;
  const start = Date.parse(startsAt);
  return !Number.isNaN(start) && start <= nowMs;
}

/** Human label for a meeting status. */
export function statusLabel(status: string | null | undefined): string {
  switch ((status ?? '').toLowerCase()) {
    case 'scheduled':
      return 'Scheduled';
    case 'cancelled':
      return 'Cancelled';
    default:
      return humanize(status);
  }
}

/**
 * Format a meeting's start instant IN THE MEETING'S OWN TIME ZONE, with the zone named.
 *
 * The zone is not decoration. The backend counts notice in calendar days localized to the MEETING's
 * zone on both instants — never the scheduler's — precisely so the count is a property of the
 * organization's calendar rather than of wherever the officer happened to be sitting. Rendering the time in
 * the reader's local zone while the sentence talks about the meeting's zone would put two different
 * days on one screen.
 *
 * Takes the two fields it reads rather than a whole `OrgBoardMeetingOut`, so the director's cross-board
 * summary (a narrower projection with the same two fields) formats through this one definition. The
 * module has already paid for the alternative: four date helpers written independently in five decode
 * modules, all carrying the same bug.
 */
export function displayMeetingStart(meeting: {
  starts_at?: string | null;
  time_zone?: string | null;
}): string {
  const iso = meeting.starts_at;
  if (!iso) return 'No date set';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'No date set';
  try {
    // ⚠ Explicit components, NOT `dateStyle`/`timeStyle`. Intl refuses to combine either of those
    // shortcuts with `timeZoneName`, throwing `Invalid option` — which this function would have
    // caught and quietly answered with the READER's zone, reintroducing the exact two-clocks bug the
    // zone is carried to prevent. It failed silently for every meeting until a test asserted the
    // rendered hour.
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: meeting.time_zone ?? undefined,
      // CBG-UX-COPY2 (A9): the generic name, "PT" all year, never PDT in one row and PST in the next.
      timeZoneName: 'shortGeneric',
    }).format(date);
  } catch {
    // An unrecognized IANA zone must not blank the date — fall back to the reader's zone and say so.
    return `${date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })} (your time)`;
  }
}

/**
 * How a director's own RSVP reads back TO THEM.
 *
 * Deliberately NOT `in-meeting-decode.ts`'s `intentLabel`, which is the officer's third-person
 * reading of the same field ("Said they would attend"). One fact, two audiences, two sentences — and
 * the director's is second-person because they are looking at their own answer. Defined once here
 * because both director surfaces render it.
 */
export function directorIntentLabel(intent: string | null | undefined): string {
  switch ((intent ?? '').toLowerCase()) {
    case 'attending':
      return 'You’re going';
    case 'not_attending':
      return 'You can’t make it';
    case 'tentative':
      return 'You’re not sure';
    default:
      return 'Answered';
  }
}

// ── Attendance intent (the RSVP) ─────────────────────────────────────────────────────────────────

/** The RSVPs for one meeting. Reads either wire shape and never throws. */
export function extractAttendanceIntents(body: unknown): OrgBoardAttendanceIntentOut[] {
  return extractList<OrgBoardAttendanceIntentOut>(body, 'data');
}

/**
 * The seats an RSVP may be recorded for.
 *
 * ⚠ **ACTIVE ONLY, and this is the exact opposite of the in-meeting attendance surface.** The
 * backend's `record_attendance_intent_on_behalf` resolves the seat through
 * `_require_active_member` and answers NOT-FOUND for one that has ended, for its own stated reason:
 * "an answer recorded against a seat that has ended would put a fact about a former director into
 * this meeting's expectations."
 *
 * That is the same rule the in-meeting record breaks deliberately, and the split is about WHICH WAY
 * TIME RUNS. An RSVP is about a meeting still AHEAD, so only a seat that will be there can answer.
 * Attendance is about a meeting that already HAPPENED, so a director removed in the autumn is still
 * recordable as having been in the room in the spring. Offering a removed seat here would produce a
 * refusal the officer cannot act on; filtering one out THERE would make a true fact unrecordable.
 *
 * No role filter, deliberately: an observer attends meetings. Role gates who may VOTE, which is the
 * in-meeting surface's own rule and not this one.
 */
export function seatsAnsweringFor(seats: readonly OrgBoardMemberOut[]): OrgBoardMemberOut[] {
  return seats.filter((seat) => seat.status === 'active');
}

/**
 * Which of the two capture classes recorded this answer.
 *
 * ⚠ **THE TWO NEVER SHARE A LABEL** (§3.7's rule, the same one the consent signature log obeys). A
 * director's own answer and an officer's entry on their behalf are different facts about how the
 * organization came to know something, and `recorded_by` is on the wire precisely so the weaker one is
 * never presented as the stronger.
 *
 * ⚠ It describes the answer that STANDS, not a history. One row exists per seat per meeting and an
 * entry of either kind replaces the other, moving `recorded_by` with it — so a seat that answered
 * for itself and was then re-entered by an officer reads as the officer's entry, which is what
 * happened.
 *
 * ⚠ **BOTH KNOWN VALUES ARE MATCHED POSITIVELY, AND AN UNKNOWN ONE DEGRADES TO NEITHER.** The enum
 * is two-valued today, so a fallback is unreachable — but the direction of an unreachable fallback
 * is still a decision, and defaulting to the director's own answer would make a third capture class
 * a future item adds present itself as the STRONGEST one this module knows. That is precisely the
 * substitution §3.7 exists to prevent, arriving by way of a value nobody wrote a branch for. An
 * unknown value therefore renders humanized — visibly not one of the two — so it reads as something
 * the surface has not been taught rather than as a claim about who answered.
 */
export function intentCaptureLine(intent: OrgBoardAttendanceIntentOut): string {
  if (intent.recorded_by === 'organization_admin') return 'Recorded on their behalf';
  // Everyone on the roster is invited, director or not (CBG-YR3-ROLES): the person's own answer.
  if (intent.recorded_by === 'director') return roles.answeredThemselves;
  return humanize(intent.recorded_by);
}

/**
 * Whether this answer is the DIRECTOR'S OWN — the thing an officer's entry would replace.
 *
 * ⚠ Positive on `director`, for the same reason {@link intentCaptureLine} matches both values
 * positively rather than falling back. The replacement warning this drives makes a claim about who
 * answered ("they answered from their own link"), so it must fire only where that is known to be
 * true. A negative test on the officer's value would fire it on an unrecognized capture class as
 * well, asserting something about a value nobody wrote a branch for.
 */
export function isDirectorOwnIntent(
  intent: OrgBoardAttendanceIntentOut | null | undefined,
): boolean {
  return intent?.recorded_by === 'director';
}

/** The RSVP rows keyed by seat, so a roster row can find its own answer without a nested scan. */
export function intentsBySeat(
  intents: readonly OrgBoardAttendanceIntentOut[],
): Map<string, OrgBoardAttendanceIntentOut> {
  return new Map(intents.map((intent) => [intent.board_member_id, intent]));
}

/** Whether this meeting is still ahead of now (drives "Upcoming" vs "Past" grouping). */
export function isUpcoming(meeting: OrgBoardMeetingOut): boolean {
  return instant(meeting.starts_at) >= Date.now();
}

/**
 * Whether a meeting's row says "Write minutes" (CBG-UX8-WRITE, frame m1): it has started
 * ({@link meetingHasStarted}), it stands (`scheduled`, matched positively, so a meeting called off or
 * an unknown status never asks), and no minutes were opened for it — neither the draft's pointer
 * (`minutes_record_id`, from the moment a draft opens) nor the sealed record's (`record_id`). Who may
 * draft them, `board_record:write`, is the caller's to ask.
 *
 * ⚠ The meetings read leaves a pointer out where the reader may not read the record it names, but a
 * meeting's OWN minutes are filed at standard confidentiality, which every reader of the meetings may
 * read, so an officer reads the pointer as it stands. A closed session's minutes hang from the
 * session, never from here. A draft discarded to start over is no minutes at all, and the read
 * leaves its pointer out too (CBG-FIX134), so its meeting asks again, as the to-do list does. One
 * edge the pointers can't show (CBG-UX8-WRITE's build check): minutes imported as a record from
 * before Givmo name no meeting, so that meeting still says "Write minutes" (CBG-FIX133).
 */
export function awaitsMinutes(
  meeting: Pick<OrgBoardMeetingOut, 'status' | 'starts_at' | 'minutes_record_id' | 'record_id'>,
  nowMs: number = Date.now(),
): boolean {
  return (
    meeting.status === 'scheduled' &&
    meetingHasStarted(meeting.starts_at, nowMs) &&
    !meeting.minutes_record_id &&
    !meeting.record_id
  );
}

/**
 * Whether the backend would accept an edit of this meeting — the one gate on "Edit". The backend
 * refuses EVERY edit (title, place, link, time) once the meeting is not upcoming: `update_meeting`
 * requires `BoardMeeting.is_upcoming` — the meeting still stands (`scheduled`, positively, so an
 * unknown status fails closed) AND its start is strictly ahead. Cancelling a past meeting, its
 * agenda and recording replies are all still accepted, so only Edit follows this (CBG-UX1 review).
 */
export function acceptsMeetingEdits(
  meeting: Pick<OrgBoardMeetingOut, 'status' | 'starts_at'>,
  nowMs: number = Date.now(),
): boolean {
  return meeting.status === 'scheduled' && instant(meeting.starts_at) > nowMs;
}

// ══ THE ONE-LINE NOTICE FACT (CBG-UX1) ═════════════════════════════════════════════════════════
//
// The meetings list and a meeting's page show the notice as ONE quiet line of arithmetic — "Scheduled
// 21 days ahead · your notice period is 10 days (Art. III §4)" — composed from the three STRUCTURED
// figures the backend sends beside its sentence (`days_given`, `period_days`, `bylaw_citation`). It is
// the two numbers side by side with the rule's citation, and nothing else: no colour, no verdict, and
// the SAME template whatever the numbers say, because a shortfall restyled or reworded would be the
// conclusion about notice that plan §3.4 bans, spelled another way. The backend's full sentence —
// which states its counting method and zone — stays on the meeting's page, verbatim, behind an info
// glyph. With no recorded period the line says so and shows no count (the sentence's own rule).
//
// A committee's meeting (CBG-YR6-MEET) is counted against the COMMITTEE's own notice period, so its
// line says "its notice period" — the row already leads with the committee's name — and, with none
// recorded, "No notice period recorded for the committee" (`committee-meetings-words.ts`).

type NoticeFigures = Pick<
  NonNullable<OrgBoardMeetingOut['notice']>,
  'bylaw_citation' | 'days_given' | 'period_days'
>;

const days = (count: number) => `${count} ${count === 1 ? 'day' : 'days'}`;

/**
 * The committee a meeting is of (CBG-YR6-MEET), by its name as the registry holds it now — served
 * beside `committee_id` on every meeting read — or null for the board's own meeting. A committee's
 * meeting whose name didn't come is still a committee's.
 */
export function meetingCommitteeName(
  meeting: { committee_id?: string | null; committee_name?: string | null } | null | undefined,
): string | null {
  if (!meeting?.committee_id) return null;
  const name = (meeting.committee_name ?? '').trim();
  return name.length > 0 ? name : cm.aCommittee;
}

/**
 * The notice as one line of arithmetic against the organization's recorded rule — or, for a committee's
 * meeting (`committee`: its name), against the committee's own.
 */
export function noticeFact(
  notice: NoticeFigures | null | undefined,
  meetingType: string | null | undefined,
  committee: string | null = null,
): string {
  const period = notice?.period_days ?? null;
  const given = notice?.days_given ?? null;
  if (committee !== null) {
    if (period === null) return cm.noNoticePeriod;
    const cited = notice?.bylaw_citation ? ` (${notice.bylaw_citation})` : '';
    if (given === null) return cm.itsNoticePeriod(period, cited);
    return given < 0
      ? cm.enteredAfter(-given, period, cited)
      : cm.scheduledAhead(given, period, cited);
  }
  if (period === null) {
    return `No notice period recorded for ${meetingTypeLabel(meetingType ?? 'regular').toLowerCase()} meetings`;
  }
  const cited = notice?.bylaw_citation ? ` (${notice.bylaw_citation})` : '';
  if (given === null) return `Your notice period is ${days(period)}${cited}`;
  // A meeting entered AFTER its date (a backfill) — the backend's sentence says "entered N days
  // after the meeting date"; the line says the same fact, never "-N days ahead".
  if (given < 0) {
    return `Entered ${days(-given)} after the meeting · your notice period is ${days(period)}${cited}`;
  }
  return `Scheduled ${days(given)} ahead · your notice period is ${days(period)}${cited}`;
}

/**
 * A meeting's day and time in the MEETING's own zone — "Tue, Oct 20, 2026 · 6:00 PM ET", or the
 * long form for a page header ("Tuesday, October 20, 2026 · 6:00 PM ET"). The day and the time are
 * two explicit-component formats for the reason {@link displayMeetingStart} records (Intl refuses
 * `dateStyle` with `timeZoneName`); an unknown zone falls back to the reader's and says so. The zone
 * is its generic name (CBG-UX-COPY2's A9): ET, CT, MT, PT and AKT all year; a zone with no daylight
 * time keeps its own (MST, HST); a zone outside the US prints its long generic name.
 */
export function meetingWhen(
  meeting: { starts_at?: string | null; time_zone?: string | null },
  form: 'short' | 'long' = 'short',
): string {
  const iso = meeting.starts_at;
  if (!iso) return 'No date set';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'No date set';
  const dayOptions: Intl.DateTimeFormatOptions = {
    weekday: form === 'long' ? 'long' : 'short',
    month: form === 'long' ? 'long' : 'short',
    day: 'numeric',
    year: 'numeric',
  };
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'shortGeneric',
  };
  try {
    const timeZone = meeting.time_zone ?? undefined;
    const day = new Intl.DateTimeFormat('en-US', { ...dayOptions, timeZone }).format(date);
    const time = new Intl.DateTimeFormat('en-US', { ...timeOptions, timeZone }).format(date);
    return `${day} · ${time}`;
  } catch {
    const day = new Intl.DateTimeFormat('en-US', dayOptions).format(date);
    const time = new Intl.DateTimeFormat('en-US', timeOptions).format(date);
    return `${day} · ${time} (your time)`;
  }
}

/**
 * A meeting's calendar day in the MEETING's own zone — "Apr 13, 2027" — for a line that names the day
 * alone (a committee's next meeting, its meetings on its page; CBG-YR6-MEET). An unknown zone reads
 * the day in the reader's.
 */
export function meetingDay(meeting: {
  starts_at?: string | null;
  time_zone?: string | null;
}): string {
  const iso = meeting.starts_at;
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  try {
    return new Intl.DateTimeFormat('en-US', {
      ...options,
      ...(meeting.time_zone ? { timeZone: meeting.time_zone } : {}),
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat('en-US', options).format(date);
  }
}

/** "Sep 9" — a reply's short date, the year only when it is not this year's. */
export function shortDate(iso: string | null | undefined, nowMs: number = Date.now()): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const sameYear = date.getFullYear() === new Date(nowMs).getFullYear();
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(date);
}

/**
 * An instant's short date in the MEETING's own zone — "Oct 3", the year only when it isn't this year's
 * there — for the History card (CBG-FIX37): its line's calendar day and its date column read in the
 * zone Board counts notice in, the zone the backend's note behind the glyph names its days in, so the
 * two never name different days. No zone, or one this build can't read, reads the reader's.
 */
export function meetingShortDate(
  iso: string | null | undefined,
  timeZone: string | null | undefined,
  nowMs: number = Date.now(),
): string {
  if (!timeZone) return shortDate(iso, nowMs);
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  try {
    const year = (ms: number) =>
      new Intl.DateTimeFormat('en-US', { year: 'numeric', timeZone }).format(ms);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      ...(year(date.getTime()) === year(nowMs) ? {} : { year: 'numeric' }),
      timeZone,
    }).format(date);
  } catch {
    return shortDate(iso, nowMs);
  }
}

function instant(iso: string | null | undefined): number {
  if (!iso) return 0;
  const value = new Date(iso).getTime();
  return Number.isNaN(value) ? 0 : value;
}
